/**
 * Ticket support #15 — lien profond filtré sur des personnes.
 *
 * Synosight (Contrôle Synolog) ouvre Synolog dans un nouvel onglet sur
 *   /schedule?persons=<email1>,<email2>[&date=YYYY-MM-DD]
 *   /logtime?persons=<email1>,<email2>[&date=YYYY-MM-DD]
 * Ce contrat d'URL est public : ne pas renommer les paramètres.
 *
 * - `persons` : e-mails séparés par des virgules (ou paramètre répété), à
 *   encoder par l'appelant (`encodeURIComponent`, sinon un « + » devient une
 *   espace). L'e-mail est le seul identifiant commun dont dispose Synosight.
 *   Résolus en `Person.id` sur la liste des personnes actives ; un e-mail
 *   inconnu est ignoré sans erreur.
 * - `date` : jour à afficher (semaine qui le contient).
 *
 * Le filtre ainsi posé est une surcharge **de session** du scope `schedule`
 * (cf. `applySessionFilter` dans useFilterStore) : jamais écrit en
 * localStorage, il disparaît avec l'onglet. Les paramètres restent dans l'URL :
 * un rechargement de l'onglet rejoue le lien.
 */

export const PERSONS_PARAM = 'persons';
export const DATE_PARAM = 'date';
/** Borne défensive (URL longue, liste des manquants d'un mois entier). */
export const MAX_DEEP_LINK_PERSONS = 200;

const EMAIL_RE = /^[^\s@,]+@[^\s@,]+$/;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDate(raw) {
  const m = DATE_RE.exec(raw ?? '');
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  // Rejette les dates qui « débordent » (2026-02-30 → 2 mars).
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) {
    return null;
  }
  return raw;
}

/**
 * Lit le lien profond d'une query string. `null` si aucun paramètre utile :
 * l'app se comporte alors exactement comme avant.
 *
 * @param {string} search ex. `window.location.search`
 * @returns {{ emails: string[], date: string|null } | null}
 */
export function parseDeepLink(search) {
  const params = new URLSearchParams(search ?? '');
  const emails = [];
  const seen = new Set();
  for (const chunk of params.getAll(PERSONS_PARAM)) {
    for (const part of chunk.split(',')) {
      const email = part.trim().toLowerCase();
      if (!email || email.length > 254 || !EMAIL_RE.test(email) || seen.has(email)) continue;
      seen.add(email);
      emails.push(email);
      if (emails.length >= MAX_DEEP_LINK_PERSONS) break;
    }
    if (emails.length >= MAX_DEEP_LINK_PERSONS) break;
  }
  const date = parseDate(params.get(DATE_PARAM));
  if (emails.length === 0 && !date) return null;
  return { emails, date };
}

/**
 * E-mails → `Person.id`, dans l'ordre du lien. Les e-mails sans personne
 * correspondante sont ignorés.
 *
 * @param {string[]} emails  normalisés (minuscules)
 * @param {Array<{ id: string, email?: string|null }>} persons
 * @returns {string[]}
 */
export function resolvePersonIds(emails, persons) {
  const idByEmail = new Map();
  for (const p of persons ?? []) {
    if (p?.email) idByEmail.set(String(p.email).trim().toLowerCase(), p.id);
  }
  const ids = [];
  for (const email of emails ?? []) {
    const id = idByEmail.get(email);
    if (id != null && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** Condition de filtre `person is [ids]` (format FilterBar), ou `null`. */
export function buildPersonCondition(ids) {
  if (!ids || ids.length === 0) return null;
  return { dimension: 'person', operator: 'is', values: [...ids] };
}

// Saisie des temps : le mode Tableau n'affiche qu'une personne. Un lien portant
// des personnes ouvre donc le Calendrier, pour l'onglet seulement — la
// préférence mémorisée (`synolog_logtime_viewmode`) n'est pas réécrite tant que
// l'utilisateur ne choisit pas lui-même un mode, ce qui lève le forçage.
let logtimeOverrideReleased = false;

/** Mode imposé par le lien à la saisie des temps (`'calendar'`), ou `null`. */
export function logtimeViewModeOverride(search) {
  if (logtimeOverrideReleased) return null;
  return parseDeepLink(search)?.emails.length ? 'calendar' : null;
}

/** L'utilisateur a choisi un mode : le lien ne l'impose plus dans cet onglet. */
export function releaseLogtimeViewModeOverride() {
  logtimeOverrideReleased = true;
}
